import { PAGEPATHS } from "../../components/Common/Constant"
import React, { useEffect, useState } from "react"
import { Row, Col, Card, CardBody } from "reactstrap"
import { useParams } from "react-router-dom" //
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import { TableHeader } from "components/Common/common";
import { setBreadcrumbItems } from "../../store/actions"
import { connect } from "react-redux"
import DataTableWithPopup from "components/longContentTable";
import ReactApexChart from 'react-apexcharts';
import "./Vulnerability.css";
import {
  ShieldAlert,
  AlertTriangle,
  Bug,
  PieChart,
  FileSearch,
  Info,
  Loader
} from 'lucide-react';

const VulnerabilityOverview = (props) => {
    document.title = "Vulnerability Overview";
    // const [scanId, setScanId] = useState(sessionStorage.getItem('scanId'))
    const { scanId: paramScanId } = useParams();
    const [scanId, setScanId] = useState(paramScanId || sessionStorage.getItem('scanId'));
    const [zaproxyTableData, setZaproxyTableData] = useState({})
    const [zaproxyTotalIssues, setZaproxyTotalIssues] = useState('')

    const [pieZaproxyOptions, setPieZaproxyOptions] = useState({
        chart: {
            type: 'donut',
            offsetX: -100
        },
        legend: {
            position: 'right',
            horizontalAlign: 'center',
            floating: true,
            offsetY: 80,
            offsetX: -20
        },
        dataLabels: {
            enabled: true,
            color: '#fff',
            // formatter: function (val, opts) {
            //     return opts.w.globals.labels[opts.seriesIndex];
            // },
            style: {
                // fontSize: '14px',
                colors: ['#fff'],
            },
        },
        labels: null,
        title: {
            text: 'Zaproxy Scan Issue Distribution',
            align: 'center',
            offsetX: 100
        }
    });

    const [pieZaproxySeries, setPieZaproxySeries] = useState([]);

    const breadcrumbItems = [
        { title: "Scan List", link: PAGEPATHS.Scanlist },
        { title: sessionStorage.getItem('domain'), link: "#" },
        { title: "Vulnerability Overview", link: "#" }
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Vulnerability Overview", breadcrumbItems)
    })

    useEffect(() => {
        const getZaproxyData = async () => {
            // Don't make API call if scanId is null or undefined
            if (!scanId) {
                console.warn('Scan ID is not available');
                return;
            }

            try {
                const { data } = await axiosInstance.get(`${ApiNames.FETCH_ZAPROXY_SCAN}/${scanId}`)
                if (data && data.status && data.data) {
                    // Safe handling of tableData with null check
                    const rawTableData = data.data.tableData || [];
                    const processedTableData = Array.isArray(rawTableData)
                        ? rawTableData.map(obj => {
                            return Object.fromEntries(
                                Object.entries(obj).map(([key, value]) => [
                                    key, typeof value === 'number' || typeof value === 'boolean'
                                        ? value.toString()
                                        : value === null || value === undefined
                                            ? 'NaN'
                                            : value
                                ])
                            );
                        })
                        : [];

                    const { chart, totalIssues } = data.data

                    // Process table data
                    const valid = processedTableData.reduce((acc, val) => {
                        if (acc.columns?.length === 0) {
                            acc.columns = Object.keys(val).reduce((a, k) => {
                                a.push({
                                    label: k,
                                    field: k,
                                    sort: 'asc',
                                    width: 150
                                })
                                return a
                            }, [])
                        }
                        acc.rows.push(val)
                        return acc
                    }, { columns: [], rows: [] })

                    setZaproxyTableData(valid)

                    // Safe handling of chart data with null check
                    if (chart && Array.isArray(chart)) {
                        setPieZaproxySeries(chart.map(c => c.count))
                        setPieZaproxyOptions((pre) => ({
                            ...pre,
                            labels: chart.map(r => r.reference)
                        }))
                    } else {
                        setPieZaproxySeries([])
                        setPieZaproxyOptions((pre) => ({
                            ...pre,
                            labels: []
                        }))
                    }

                    setZaproxyTotalIssues(totalIssues || 0)
                }
            } catch (e) {
                console.error('Error fetching Zaproxy data:', e);
                // Set empty states on error
                setZaproxyTableData({ columns: [], rows: [] })
                setPieZaproxySeries([])
                setPieZaproxyOptions((pre) => ({
                    ...pre,
                    labels: []
                }))
                setZaproxyTotalIssues(0)
            }
        }
        getZaproxyData()
    }, [scanId])

    return (
        <React.Fragment>
            <div className="vulnerability-page">
                {/* Page Header */}
                <div className="vulnerability-header">
                    <div className="vulnerability-header-content">
                        <div className="vulnerability-icon-wrapper">
                            <ShieldAlert className="vulnerability-icon" />
                        </div>
                        <div className="vulnerability-header-text">
                            <h1>Vulnerability Overview</h1>
                            <p>Security vulnerability assessment and OWASP ZAP scan results for {sessionStorage.getItem('domain')}</p>
                        </div>
                    </div>
                </div>

                {/* Info Section */}
                <div className="vulnerability-info-box">
                    <div className="vulnerability-info-header">
                        <div className="vulnerability-info-icon-wrapper">
                            <Info className="vulnerability-info-icon" />
                        </div>
                        <h3>About This Scan</h3>
                    </div>
                    <div className="vulnerability-info-content">
                        <p>
                            This vulnerability scan uses OWASP ZAProxy to identify security issues in your web application.
                            ZAP actively tests for common vulnerabilities like XSS, SQL injection, and other OWASP Top 10 risks.
                        </p>
                        <p>
                            Results are categorized by severity and reference to help prioritize remediation efforts.
                        </p>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="vulnerability-summary">
                    <div className="vulnerability-summary-card">
                        <div className="vulnerability-summary-icon-wrapper red">
                            <AlertTriangle className="vulnerability-summary-icon red" />
                        </div>
                        <div className="vulnerability-summary-content">
                            <h3>{zaproxyTotalIssues || '0'}</h3>
                            <p>Total Vulnerabilities</p>
                        </div>
                    </div>

                    <div className="vulnerability-summary-card">
                        <div className="vulnerability-summary-icon-wrapper orange">
                            <Bug className="vulnerability-summary-icon orange" />
                        </div>
                        <div className="vulnerability-summary-content">
                            <h3>{zaproxyTableData.rows ? zaproxyTableData.rows.length : '0'}</h3>
                            <p>Unique Issues</p>
                        </div>
                    </div>

                    <div className="vulnerability-summary-card">
                        <div className="vulnerability-summary-icon-wrapper blue">
                            <FileSearch className="vulnerability-summary-icon blue" />
                        </div>
                        <div className="vulnerability-summary-content">
                            <h3>{pieZaproxyOptions.labels ? pieZaproxyOptions.labels.length : '0'}</h3>
                            <p>Issue Categories</p>
                        </div>
                    </div>
                </div>

                {/* Chart and Total Issues Section */}
                <div className="vulnerability-chart-section">
                    {/* Total Issues Card */}
                    <div className="total-issues-card">
                        <div className="total-issues-icon-wrapper">
                            <AlertTriangle className="total-issues-icon" />
                        </div>
                        <div className="total-issues-number">
                            {zaproxyTotalIssues || '0'}
                        </div>
                        <div className="total-issues-label">
                            Total Issues Found
                        </div>
                    </div>

                    {/* Chart Card */}
                    <div className="chart-card">
                        <div className="chart-header">
                            <PieChart className="chart-header-icon" />
                            <h3>Issue Distribution by Reference</h3>
                        </div>
                        {pieZaproxyOptions.labels && pieZaproxySeries.length !== 0 ? (
                            <ReactApexChart
                                options={{
                                    ...pieZaproxyOptions,
                                    chart: {
                                        type: 'donut'
                                    },
                                    legend: {
                                        position: 'bottom',
                                        horizontalAlign: 'center',
                                        floating: false
                                    },
                                    title: {
                                        text: undefined
                                    }
                                }}
                                series={pieZaproxySeries}
                                type="donut"
                                height={350}
                            />
                        ) : (
                            <div className="vulnerability-empty-state">
                                <PieChart className="vulnerability-empty-icon" />
                                <p>No chart data available</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Data Table Section */}
                <div className="vulnerability-data-section">
                    <div className="vulnerability-section-header">
                        <ShieldAlert className="section-header-icon" />
                        <h2>ZAProxy Scan Results</h2>
                        <span className="scan-id">Scan ID: {scanId}</span>
                    </div>
                    {zaproxyTableData && zaproxyTableData.rows && zaproxyTableData.rows.length > 0 ? (
                        <DataTableWithPopup
                            noBottomColumns={false}
                            thirdPartyData={zaproxyTableData}
                        />
                    ) : (
                        <div className="vulnerability-empty-state">
                            <ShieldAlert className="vulnerability-empty-icon" />
                            <p>No vulnerability scan data available</p>
                        </div>
                    )}
                </div>
            </div>
        </React.Fragment >
    )
}
export default connect(null, { setBreadcrumbItems })(VulnerabilityOverview)